
import {
  watch, defineComponent, ref, nextTick, onMounted, onUnmounted,
} from 'vue';

import * as echarts from 'echarts';

import html2canvas from 'html2canvas';
import { downLoad, } from '@/utils/index';

export default defineComponent({
  props: {
    airType: {
      type: String,
      default: () => 'aqi',
      require: true,
    },
    roseDataList: {
      type: Object,
      default: Object,
      require: true,
    },
  },
  setup (props) {
    // 要素对应文字
    const factorKey = {
      pm25: '<span>PM<sub>2.5</sub></span>',
      pm10: '<span>PM<sub>10</sub></span>',
      so2: '<span>SO<sub>2</sub></span>',
      no2: '<span>NO<sub>2</sub></span>',
      co: 'CO',
      o3: '<span>O<sub>3</sub></span>',
      aqi: 'AQI',
      tvoc: 'TVOC',
    };

    const unit = () => {
      if (props.airType === 'aqi') {
        return '';
      }

      if (props.airType === 'co') {
        return 'mg/m³';
      }

      return 'μg/m³';
    };

    const analysisChart = ref<any>(null);
    const noDataShow = ref<boolean>(false);
    let chart: any = null;

    const option = {
      angleAxis: {
        type: 'category',
        data: ['北风',
          '东北风',
          '东风',
          '东南风',
          '南风',
          '西南风',
          '西风',
          '西北风',],
        boundaryGap: false,
      },
      radiusAxis: {},
      polar: {},
      series: [
        {
          type: 'bar',
          data: [],
          coordinateSystem: 'polar',
          itemStyle: {
            color: '#AAD663',
          },
          name: 'A',
          stack: 'a',
          emphasis: {
            focus: 'series',
          },
        },
        {
          type: 'bar',
          data: [],
          coordinateSystem: 'polar',
          itemStyle: {
            color: '#F1CF58',
          },
          name: 'B',
          stack: 'a',
          emphasis: {
            focus: 'series',
          },
        },
        {
          type: 'bar',
          data: [],
          coordinateSystem: 'polar',
          itemStyle: {
            color: '#E79458',
          },
          name: 'C',
          stack: 'a',
          emphasis: {
            focus: 'series',
          },
        },
        {
          type: 'bar',
          data: [],
          coordinateSystem: 'polar',
          itemStyle: {
            color: '#E79458',
          },
          name: 'D',
          stack: 'a',
          emphasis: {
            focus: 'series',
          },
        },
        {
          type: 'bar',
          data: [],
          coordinateSystem: 'polar',
          itemStyle: {
            color: '#E79458',
          },
          name: 'E',
          stack: 'a',
          emphasis: {
            focus: 'series',
          },
        },
        {
          type: 'bar',
          data: [],
          coordinateSystem: 'polar',
          itemStyle: {
            color: '#E79458',
          },
          name: 'F',
          stack: 'a',
          emphasis: {
            focus: 'series',
          },
        },
      ],
      tooltip: {
        show: true,
        formatter: (params: any) => `<div style="text-align: left;"><div>风向: ${params.name}
        </div><div>${factorKey[props.airType]}: ${params.value}${unit()}</div></div>`
        ,
      },
      legend: {
        show: false,
        data: ['A',
          'B',
          'C',],
      },
    };

    /**
     *  配置线状图
     * @param {any} val 配置项
     * @return {void}
     */
    const setChart = (val: any) => {
      nextTick(() => {
        if (val && Object.keys(chart).length) {
          // 不和之前的option合并
          chart.setOption(val, true);
        }
      });
    };

    /**
     *  重置图表
     * @param {any} val value
     * @return {void}
     */
    const reSize = (): any => {
      if (Object.keys(chart).length) {
        chart.resize();
      }
    };

    const drawChart = (val: any): void => {
      setChart(option);

      for (let i = 0; i < 6; i++) {
        option.series[i].data = val[props.airType][i];
      }

      reSize();
    };

    /**
     * 初始化图表
     * @returns {void}
     */
    const initCharts = () => {
      const chartContainer = document.getElementById('rose-container') as HTMLElement;

      chart = echarts.init(chartContainer);
      window.addEventListener('resize', reSize);
    };

    /**
     *  下载图表
     * @return {void}
     */
    const downloadChart = (): any => {
      if (analysisChart.value) {
        html2canvas(analysisChart.value, {
          width: analysisChart.value.clientWidth,
          height: analysisChart.value.clientHeight,
          scrollY: 0,
          scrollX: 0,
          scale: 0.8,
          useCORS: true,
          allowTaint: false,
        }).then((canvas) => {
          const base64 = canvas.toDataURL('image/png');

          downLoad(base64, `${props.airType.toUpperCase()}浓度与风向频次关系图`);
        });
      }
    };

    watch(() => props.roseDataList, (cur: any) => {
      if (Object.keys(cur).length) {
        noDataShow.value = false;
        nextTick(() => {
          drawChart(cur);
        });
      } else {
        noDataShow.value = true;
      }
    }, {
      deep: true,
      immediate: true,
    });

    watch(() => props.airType, (cur: string) => {
      if (cur) {
        setTimeout(() => {
          drawChart(props.roseDataList);
        });
      }
    });

    /**
     * 挂载图表
     */
    onMounted(() => {
      initCharts();
    });

    /**
     * 销毁图表
     */
    onUnmounted(() => {
      window.removeEventListener('resize', reSize);

      echarts.dispose(chart);
      chart = null;
    });

    return {
      props,
      factorKey,
      noDataShow,
      analysisChart,
      downloadChart,
    };
  },
});

